'use client';

import React, { useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { TopAd } from '@/tracking/ads/components/TopAd';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { Layout } from '@/components/resort/Layout/Layout';
import { Main } from '@/components/resort/Layout/Main';
import { BottomMountains } from '@/components/blocks/BottomMountains';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { Heading } from '@/components/ui/heading';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { HomeHero } from '@/components/home/HomeHero';
import { AiHomeHero } from '@/components/home/AiHomeHero';
import {
  IBackgroundImages,
  IFeaturedResort,
  INews,
  IPopularSearch,
  IResortTopRated,
  ISnowWebcam,
  IFirstHandReportsResponse,
  IMostReportedSnowfallResponse,
} from '@/api/home/types';
import { BestResorts } from '@/components/home/BestResorts';
import { NewsList } from '@/components/home/NewsList';
import { Routes } from '@/routes';
import { getPortalOptions } from '@/config/getPortalConfig';
import IntegrityError from '@/page-components/400';
import GlobalTemplateWeSkiWidget from '@/components/common/PlanYourTrip/WeSki/GlobalTemplateWeSkiWidget';
import CamsAndSnowfallSection from '@/components/home/CamsAndSnowfallSection';
import { NewGuideSlider } from '@/components/resort/NewGuideSlider';
import { FeaturedResort } from '@/components/home/FeaturedResort';
import { IResortPathInfo, IDescriptionsResort } from '@/api/resort/types';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';

export type HomePageProps = {
  popularSearch: IPopularSearch,
  backgroundImage: IBackgroundImages,
  snowWebcams: Array<ISnowWebcam>,
  resortTopRatingOverall: Array<IResortTopRated>,
  lastNews: Array<INews> | null,
  featuredResort: IFeaturedResort | null,
  featuredResortRegionResorts?: Array<IResortPathInfo>,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort>,
  firstHandReports?: IFirstHandReportsResponse,
  regionResorts?: Array<IResortPathInfo>,
  type?: 'global' | undefined,
  mostReportedSnowfall?: IMostReportedSnowfallResponse | null,
  webcamsLinkRegionSlug?: string;
  suggestions?: string[] | null;
} & RouteAndAnalyticsProps;

export const DEBUG_TAG = 'home-page';
export const TEMPLATE_ID = '1.0';
export const TEMPLATE_NAME = 'Home Page';

export default function HomePage({
  popularSearch,
  backgroundImage,
  snowWebcams,
  resortTopRatingOverall,
  mostReportedSnowfall,
  firstHandReports,
  regionResorts,
  lastNews,
  featuredResort,
  featuredResortRegionResorts,
  featuredResortDescriptionTabs,
  type,
  webcamsLinkRegionSlug,
  suggestions,
}: HomePageProps) {
  const { hasNews } = getPortalOptions();

  const { t } = useOTSTranslation('common');

  if (!popularSearch || !backgroundImage) {
    return <IntegrityError />;
  }

  const router = useRouter();
  const searchParams = useSearchParams();
  const q = searchParams?.get('q') ?? undefined;
  const [searchQuery, setSearchQuery] = useState<string | undefined>(q);

  const clearSearchQuery = useCallback(async () => {
    if (global?.location?.search) {
      router.replace(global?.location?.pathname);
    }
    setSearchQuery(undefined);
  }, [router]);

  const locale = getLocale() as ELocale;
  const isShowingAiHomeHero = locale === ELocale.EN_US || locale === ELocale.EN_GB;

  return (
    <div style={{ overflowX: 'hidden' }}>
      <AdUnitContext adType="home" adTKey="other home">
        <HeaderNav current={HeaderNavCurLink.MAIN} />
        <TopAd id="home" />
        {isShowingAiHomeHero ? (
          <AiHomeHero
            backgroundImage={backgroundImage}
            suggestions={suggestions || []}
            searchQuery={searchQuery}
            clearSearchQuery={clearSearchQuery}
          />
        ) : (
          <HomeHero
            backgroundImage={backgroundImage}
            popularSearch={popularSearch}
            searchQuery={searchQuery}
            clearSearchQuery={clearSearchQuery}
          />
        )}
        <Container width="xl-only" className="content-container home-page-container">
          <Layout className="layout-container-noback">
            <Main>
              {/* The page's one real H1. It carries the head term the winter
                  home page ranks for ("Ski Reports & Snow Forecast"), which
                  the hero's brand line does not. Production renders it in
                  exactly this position — a visible centred heading above the
                  webcams — and the hero above stays decorative. */}
              <Heading level={1} size="3xl" className="mb-6 mt-2 text-center lg:mt-0">
                {t('homePage.h1Title')}
              </Heading>
              <CamsAndSnowfallSection
                snowWebcamsProps={{
                  headerScope: 'h2',
                  showSnowReport: true,
                  snowWebcams,
                  imageLink: Routes.resortSnowReport,
                  titleLink: Routes.resortSnowReport,
                  header: t('common.featuredWebcams'),
                }}
                currentRegionSlug={webcamsLinkRegionSlug}
                mostReportedSnowfallItems={mostReportedSnowfall?.data ?? null}
              />
              {lastNews && hasNews && (
                <NewsList
                  title={t('homePage.latestNews')}
                  news={lastNews}
                />
              )}

              <GlobalTemplateWeSkiWidget />
              {firstHandReports && firstHandReports.reportsList && firstHandReports.reportsList.length > 0 && (
                <NewGuideSlider
                  title={t('nav.tab.fhr')}
                  slides={[]}
                  reports={firstHandReports.reportsList}
                  regionResorts={regionResorts || []}
                />
              )}
              {featuredResort && (
                <FeaturedResort
                  resort={featuredResort}
                  regionResorts={featuredResortRegionResorts}
                  resortDescriptionTabs={featuredResortDescriptionTabs}
                  utmCampaign="homepage_widget"
                />
              )}
              {resortTopRatingOverall && (
                <BestResorts
                  pageType={type}
                  resortTopRatingOverall={resortTopRatingOverall}
                  isUsedInHomePage
                />
              )}
            </Main>
            {/* JSON-LD renders from the RSC page (see app/page.tsx). */}
          </Layout>
        </Container>
        <BottomMountains />
        <Footer />
      </AdUnitContext>
    </div>
  );
}
